import { Box } from "@chakra-ui/react";
import { Checkbox, Col, Form, Input, Modal, Radio, Row, Select } from "antd";
import { Rule, RuleObject } from "antd/lib/form";
import useAvailablePackageTypes from "hooks/useAvailablePackageTypes";
import usePackageUpdate from "hooks/usePackageUpdate";
import { Owners } from "models/applicationUsers";
import { PackageTypeOption } from "models/common";
import { PackageStatus, PackageType, StaffPackage } from "models/packages";
import { useEffect, useState } from "react";
import { centsToDollars, dollarsToCents } from "util/helpers";
import { mapOwners, ownershipUpdate, ownershipValidator } from "util/ownership";
import OwnershipSelector from "../OwnershipSelector";

interface EditPackageForm {
    owners: Owners;
    packageTypeId: string;
    status: PackageStatus;
    processingFeeInCents: number | null;
    processingFee: string | null;
    priority: boolean;
    sendProcessingFee?: boolean;
    trackingNumberSuffix: string | null;
}

const EditPackageModal = (props: {
    open: boolean;
    package: StaffPackage;
    onClose: (edited?: boolean) => void;
}) => {
    const { updatePackage } = usePackageUpdate();
    const [working, setWorking] = useState<boolean>(false);
    const [form] = Form.useForm<EditPackageForm>();
    const { data: types } = useAvailablePackageTypes(false, props.package.packageTypeId);
    const [showFee, setShowFee] = useState<boolean>(props.package.processingFeeInCents > 0);
    const [currentPkgType, setCurrentPkgType] = useState<PackageType>();

    useEffect(() => {
        if (types) {
            setCurrentPkgType(types.find(t => t.id === props.package.packageTypeId));
        }
    }, [props.package.packageTypeId, types]);

    const initalValues: EditPackageForm = {
        ...props.package,
        owners: mapOwners(props.package),
        status: props.package.packageStatus,
        processingFee: centsToDollars(props.package.processingFeeInCents, false),
        sendProcessingFee: props.package.processingFeeInCents > 0,
        trackingNumberSuffix: props.package.trackingNumberSuffix
    };

    const togglePackageTypeOptions = (_: string, option: PackageTypeOption | PackageTypeOption[]) => {
        const opt = option as PackageTypeOption;
        const sendProcessingFee = opt.packageType.processingFeeInCents > 0;

        // if the current and selected package types have mismatched support for silent accounts,
        // clear ownership to force the user to re-select who owns the package
        const owners: Owners =
            currentPkgType!.supportSilentAccounts === opt.packageType.supportSilentAccounts
                ? form.getFieldValue('owners')
                : { customers: [], silentUsers: [] }

        setShowFee(sendProcessingFee);
        setCurrentPkgType(opt.packageType);

        form.setFieldsValue({
            sendProcessingFee,
            processingFee: sendProcessingFee ? centsToDollars(opt.packageType.processingFeeInCents, false) : null,
            owners
        });
    }

    const closeAndResetModal = () => {
        props.onClose();
        form.resetFields();

        setShowFee(props.package.processingFeeInCents > 0);
    };

    const submitForm = () => {
        form.validateFields().then(async (data) => {
            setWorking(true);

            try {
                await updatePackage({
                    id: props.package.id,
                    owners: ownershipUpdate(initalValues.owners, data.owners),
                    packageTypeId: data.packageTypeId,
                    packageStatus: data.status,
                    priority: data.priority,
                    processingFeeInCents: data.sendProcessingFee
                        ? data.processingFeeInCents
                        : null,
                    trackingNumberSuffix: data.trackingNumberSuffix?.trim() !== ""
                        ? data.trackingNumberSuffix
                        : null
                });

                props.onClose(true);
            } finally {
                setWorking(false);
            }
        });
    };

    const feeValidator = async (
        _: RuleObject,
        price: number
    ): Promise<void> => {
        if (isNaN(price)) {
            return Promise.reject(new Error("Not a valid price!"));
        }

        form.setFieldsValue({ processingFeeInCents: Math.max(price, 0) });
        return Promise.resolve();
    };

    const title =
        props.package.friendlyId > 0
            ? `Edit Package #${props.package.friendlyId}`
            : "Edit Package";

    const isReceived = props.package.packageStatus === PackageStatus.Received;

    return (
        <Modal
            title={title}
            closable={false}
            keyboard={false}
            maskClosable={false}
            destroyOnClose={true}
            open={props.open}
            confirmLoading={working}
            cancelButtonProps={{ disabled: working }}
            okText={working ? "Saving..." : "Update"}
            onOk={submitForm}
            onCancel={closeAndResetModal}
        >
            <Form
                form={form}
                disabled={working}
                initialValues={initalValues}
                layout="vertical"
            >
                <Form.Item label="Package Type" name="packageTypeId">
                    <Select
                        disabled={types?.length === 0}
                        onChange={togglePackageTypeOptions}
                        options={types?.map(t =>
                        ({
                            label: t.enabled ? t.internalName : `${t.internalName} (Inactive)`,
                            value: t.id, packageType: t,
                            disabled: !t.enabled
                        }))} />
                </Form.Item>
                <Form.Item
                    name="owners"
                    rules={[
                        { validator: async (_: Rule, owners: Owners) => await ownershipValidator(currentPkgType!.supportSilentAccounts, owners) }
                    ]}
                >
                    <OwnershipSelector
                        supportSilentAccounts={currentPkgType?.supportSilentAccounts}
                        allowMultiple={currentPkgType?.allowMultipleOwners}
                    />
                </Form.Item>
                <Box hidden as={Form.Item} name="sendProcessingFee"><Input hidden /></Box>
                {showFee && <Form.Item label="Processing Fee" name="processingFee" rules={[
                    {
                        required: true,
                        validator: feeValidator,
                        transform: dollarsToCents,
                    },
                ]}>
                    <Input
                        prefix="$"
                        suffix="Per Card, USD"
                    />
                </Form.Item>}
                <Row>
                    <Col span={16}>
                        <Form.Item label="Package Status" name="status">
                            <Radio.Group optionType="button" buttonStyle="solid">
                                <Radio value={PackageStatus.Ebay}>eBay</Radio>
                                <Radio value={PackageStatus.Received}>Received</Radio>
                                <Radio value={PackageStatus.Returned}>Returned</Radio>
                            </Radio.Group>
                        </Form.Item>
                    </Col>
                    {isReceived && <Col span={8}>
                        <Form.Item label="Priority Processing?" name="priority" valuePropName="checked">
                            <Checkbox />
                        </Form.Item>
                    </Col>}
                </Row>
                <Form.Item
                    label="Tracking Number (Last 4)"
                    name="trackingNumberSuffix"
                    rules={[{ min: 4, max: 4, message: "Must be 4 characters" }]}>
                    <Input />
                </Form.Item>
                <Form.Item
                    name="processingFeeInCents"
                    className="has-hidden-input"
                >
                    <Input type="hidden" />
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default EditPackageModal;
